/**
 * 上海CA（SHECA）CA 登录封装。
 *
 * 移植自产品 stdv6-web `components/CaLoad/ca_chrome.js` 的 SHECA 实现（产品支持多家 CA，
 * 此处只保留上海CA相关代码）。与 CFCA 的 ActiveX 方案不同，上海CA 走「SDK 脚本 + 本机
 * WebSocket 驱动」：
 *  1. 动态加载 `public/static/pki/sheca-sdk.js`（加载后挂全局 shcaSecKit）；
 *  2. 探测本机 CA 驱动服务 ws://127.0.0.1:18454 是否可用；
 *  3. initService 枚举设备 → setCryptoDevice → verifyUserPin 校验 CA 密码 → getCert 取签名证书；
 *  4. getCertDetailSync 读取证书明细（机构名称 / 有效期 / 序列号等）。
 *
 * 证书序列号 serialNumber 即上海CA续签接口 shcaAuthRenew 的必传参数 caSerialNo。
 */

/**
 * SDK 地址：public/static/pki/sheca-sdk.js（构建后原样输出，hash 路由下按根路径解析）。
 * 目录名用 pki 而非 ca：部分环境 nginx 有 `(^|/)ca` 前缀代理规则（把 /ca 转发给后端服务），
 * 会把 /static/ca/* 一并劫持返回 500，导致 SDK 加载失败。
 */
const SDK_SRC = './static/pki/sheca-sdk.js'
/** 本机 CA 驱动服务地址（产品同值，勿改） */
const SERVICE_WS = 'ws://127.0.0.1:18454'

const ERR_MSG = {
  browser: '当前浏览器不支持CA功能，请使用 Chrome / Firefox / Edge / IE10 及以上版本浏览器',
  websocket: '当前浏览器不支持 WebSocket，请更换浏览器',
  sdk: '上海CA控件加载失败，请检查网络后重试',
  init: '证书初始化失败，请检查驱动是否安装、CA是否插好！',
  pin: 'CA密码错误',
}

/** 证书明细类型 → SHECA SDK itemNo（产品同值） */
const ITEM_NO = {
  SubjectDN: 14,
  SubjectCN: 14,
  SerialNumber: 2,
  IssuerItem: 5,
  CertBeginDate: 11,
  CertEndDate: 12,
}

/** 证书类型（产品口径：上海CA CA 登录固定按个人证书处理） */
export const CERT_TYPE_TEXT = { 1: '企业证书', 2: '个人证书' }
const CERT_TYPE_PERSONAL = '2'

/** 读取浏览器名称与版本；识别不出返回 null */
function getBrowserInfo() {
  const ua = navigator.userAgent.toLowerCase()
  const isIE = ua.indexOf('compatible') > -1 && ua.indexOf('msie') > -1
  const isIE11 = ua.indexOf('trident') > -1 && ua.indexOf('rv:11.0') > -1
  const isEdge = ua.indexOf('edge') > -1
  const isChrome = ua.indexOf('chrome') > -1 && ua.indexOf('safari') > -1
  const isFirefox = ua.indexOf('firefox') > -1

  if (isIE) {
    const m = /msie ([\d.]+);/.exec(ua)
    return { name: 'IE', version: m ? m[1] : '7' }
  }
  if (isIE11) return { name: 'IE', version: '11' }
  if (isEdge) {
    const m = /edge\/([\d.]+)/.exec(ua)
    return { name: 'Edge', version: m ? m[1] : '' }
  }
  if (isChrome) {
    const m = /chrome\/([\d.]+)/.exec(ua)
    return { name: 'Chrome', version: m ? m[1] : '' }
  }
  if (isFirefox) {
    const m = /firefox\/([\d.]+)/.exec(ua)
    return { name: 'Firefox', version: m ? m[1] : '' }
  }
  return null
}

/**
 * 浏览器兼容校验（产品口径）：Chrome>50 / Firefox>52 / Edge / IE>=10。
 * 不满足时抛错，message 可直接 toast。
 */
export function checkShecaBrowser() {
  const info = getBrowserInfo()
  if (!info) throw new Error(ERR_MSG.browser)
  const version = parseInt(info.version, 10) || 0
  const ok =
    (info.name === 'Chrome' && version > 50) ||
    (info.name === 'Firefox' && version > 52) ||
    info.name === 'Edge' ||
    (info.name === 'IE' && version >= 10)
  if (!ok) throw new Error(ERR_MSG.browser)
}

/** 动态加载 SHECA SDK（幂等：已加载直接返回，失败后允许重试） */
let sdkLoading = null
function loadSdk() {
  if (window.shcaSecKit) return Promise.resolve()
  if (sdkLoading) return sdkLoading
  sdkLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SDK_SRC
    script.async = true
    script.onload = () => {
      if (window.shcaSecKit) {
        resolve()
      } else {
        sdkLoading = null
        reject(new Error(ERR_MSG.sdk))
      }
    }
    script.onerror = () => {
      sdkLoading = null
      reject(new Error(ERR_MSG.sdk))
    }
    document.head.appendChild(script)
  })
  return sdkLoading
}

/** yyyyMMddHHmmss → yyyy-MM-dd HH:mm:ss（SDK 起始/截止日期原始格式） */
function formatCertDate(value) {
  const s = String(value || '')
  if (!/^\d{14}$/.test(s)) return s
  return (
    s.substr(0, 4) +
    '-' +
    s.substr(4, 2) +
    '-' +
    s.substr(6, 2) +
    ' ' +
    s.substr(8, 2) +
    ':' +
    s.substr(10, 2) +
    ':' +
    s.substr(12, 2)
  )
}

export default class ShecaCa {
  /**
   * @param {string} password CA 密码（UKey PIN）
   */
  constructor(password) {
    this.password = password
    this.CaAgent = null
    this.signCert = ''
  }

  /** 加载 SDK 并取得控件实例 */
  async loadDriver() {
    checkShecaBrowser()
    if (!('WebSocket' in window)) throw new Error(ERR_MSG.websocket)
    await loadSdk()
    this.CaAgent = window.shcaSecKit.getInstance()
  }

  /** 登录：探测驱动服务 → 初始化设备 → 校验密码 → 取签名证书 */
  async init() {
    if (!this.CaAgent) throw new Error(ERR_MSG.init)
    await this.checkService()
    await this.initService()
    await this.verifyUserPin()
    await this.getSignCert()
    // 验证证书策略（产品原逻辑：仅调用，不校验返回值）
    try {
      this.CaAgent.getCertDetailSync({ cert: this.signCert, itemNo: 66 })
    } catch (e) {
      // 忽略：不影响后续证书信息读取
    }
  }

  /** 探测本机 CA 驱动服务是否已启动（未装驱动 / 未插 CA 时给出明确提示） */
  checkService() {
    return new Promise((resolve, reject) => {
      let ws
      try {
        ws = new WebSocket(SERVICE_WS)
      } catch (e) {
        reject(new Error(ERR_MSG.init))
        return
      }
      ws.onopen = () => {
        ws.close()
        resolve()
      }
      ws.onerror = () => reject(new Error(ERR_MSG.init))
    })
  }

  /** 枚举本机加密设备并选中第一台 */
  initService() {
    return new Promise((resolve, reject) => {
      this.CaAgent.initService({
        asymid: 1,
        res: (data) => {
          if (Number(data.ret) !== 0) {
            reject(new Error(ERR_MSG.init))
            return
          }
          try {
            const devices = JSON.parse(data.str)
            if (!devices || !devices.length) throw new Error()
            this.CaAgent.setCryptoDevice(devices[0])
            resolve()
          } catch (e) {
            reject(new Error(ERR_MSG.init))
          }
        },
      })
    })
  }

  /** 校验 CA 密码（UKey PIN） */
  verifyUserPin() {
    return new Promise((resolve, reject) => {
      this.CaAgent.verifyUserPin({
        asymid: 1,
        pwd: this.password,
        res: (data) => {
          if (Number(data.ret) === 0) resolve()
          else reject(new Error(ERR_MSG.pin))
        },
      })
    })
  }

  /** 取签名证书（base64），并缓存到实例 */
  getSignCert() {
    return new Promise((resolve, reject) => {
      this.CaAgent.getCert({
        asymid: 1,
        usage: 2,
        res: (data) => {
          if (Number(data.ret) === 0) {
            this.signCert = data.str
            resolve(data.str)
          } else {
            reject(new Error(ERR_MSG.init))
          }
        },
      })
    })
  }

  /**
   * 读取证书明细。
   * @param {'SubjectDN'|'SubjectCN'|'SerialNumber'|'IssuerItem'|'CertBeginDate'|'CertEndDate'} type
   * @returns {Promise<string>} 日期类已格式化为 yyyy-MM-dd HH:mm:ss
   */
  async getSignCertDetail(type) {
    const itemNo = ITEM_NO[type]
    if (!itemNo) throw new Error('不支持的证书信息类型：' + type)
    const cert = await this.getSignCert()
    const value = this.CaAgent.getCertDetailSync({ cert, itemNo })
    return itemNo === ITEM_NO.CertBeginDate || itemNo === ITEM_NO.CertEndDate
      ? formatCertDate(value)
      : value
  }

  /**
   * 读取弹框展示所需的证书信息。
   * @returns {Promise<{subjectCN: string, subjectDN: string, serialNumber: string,
   *          certBeginDate: string, certEndDate: string, certType: string}>}
   */
  async getCertInfo() {
    // 逐项读取（SDK 指令串行执行，勿并发）
    const subjectCN = await this.getSignCertDetail('SubjectCN')
    const subjectDN = await this.getSignCertDetail('SubjectDN')
    const serialNumber = await this.getSignCertDetail('SerialNumber')
    const certBeginDate = await this.getSignCertDetail('CertBeginDate')
    const certEndDate = await this.getSignCertDetail('CertEndDate')
    return {
      subjectCN,
      subjectDN,
      serialNumber,
      certBeginDate,
      certEndDate,
      // 产品口径：非 CFCA（上海CA）CA 登录固定按个人证书处理
      certType: CERT_TYPE_PERSONAL,
    }
  }
}
